/**
 * Centralized public prospect page configuration.
 * Edit contact + FR/EN marketing copy here. Do not put validation/security rules in this file.
 */

export const publicProspectPageConfig = Object.freeze({
  contact: Object.freeze({
    name: 'Michael Marceau',
    email: 'mmarceau@dmii.ca',
    emailHref: 'mailto:mmarceau@dmii.ca',
    phoneDisplay: '(514) 654-5030',
    phoneHref: 'tel:+15146545030',
    initials: 'MM',
  }),

  /** Fixed Maps Embed options — never accept zoom from URL/query/campaign/property input. */
  map: Object.freeze({
    zoom: 19,
  }),

  guide: Object.freeze({
    enabled: false,
    title: Object.freeze({
      fr: "Guide d'amélioration de la rentabilité",
      en: 'Profitability improvement guide',
    }),
    subtitle: Object.freeze({
      fr: "12 leviers pour augmenter la valeur d'un multilogement",
      en: '12 levers to increase the value of a multi-unit building',
    }),
  }),

  trustIndicators: Object.freeze([
    Object.freeze({ id: 'free', icon: 'gift', fr: 'Gratuit', en: 'Free' }),
    Object.freeze({ id: 'time', icon: 'clock', fr: '3 minutes', en: '3 minutes' }),
    Object.freeze({ id: 'noCommitment', icon: 'leaf', fr: 'Sans engagement', en: 'No commitment' }),
    Object.freeze({ id: 'confidential', icon: 'lock', fr: 'Confidentiel', en: 'Confidential' }),
  ]),

  objectives: Object.freeze([
    Object.freeze({
      intent: 'profitability_guide',
      requiresGuide: true,
      visual: 'default',
      fr: Object.freeze({
        label: 'Améliorer sa rentabilité',
        description: 'Recevez le guide des leviers de rentabilité.',
      }),
      en: Object.freeze({
        label: 'Improve profitability',
        description: 'Receive the profitability levers guide.',
      }),
    }),
    Object.freeze({
      intent: 'property_value',
      requiresGuide: false,
      visual: 'default',
      fr: Object.freeze({
        label: 'Combien vaut mon immeuble?',
        description: 'Demandez une estimation préliminaire confidentielle.',
      }),
      en: Object.freeze({
        label: 'What is my building worth?',
        description: 'Request a confidential preliminary estimate.',
      }),
    }),
    Object.freeze({
      intent: 'property_sale',
      requiresGuide: false,
      visual: 'sale',
      fr: Object.freeze({
        label: 'Je veux vendre mon immeuble',
        description: 'Discussion confidentielle — possibilité d’offre directe.',
      }),
      en: Object.freeze({
        label: 'I want to sell my building',
        description: 'Confidential discussion — possible direct offer.',
      }),
    }),
  ]),

  forms: Object.freeze({
    profitability_guide: Object.freeze({
      fr: Object.freeze({
        heading: "Guide d'amélioration de la rentabilité",
        support: "12 leviers pour augmenter la valeur d'un multilogement. Nom complet et courriel requis.",
        cta: 'Recevoir le guide',
      }),
      en: Object.freeze({
        heading: 'Profitability improvement guide',
        support: '12 levers to increase multi-unit value. Full name and email required.',
        cta: 'Receive the guide',
      }),
    }),
    property_value: Object.freeze({
      fr: Object.freeze({
        heading: 'Combien vaut mon immeuble?',
        support:
          'Notre équipe examinera les renseignements transmis et préparera une estimation préliminaire. Ce n’est pas une évaluation instantanée automatisée, ni une évaluation certifiée. Sans obligation.',
        cta: 'Recevoir mon estimation préliminaire',
        stepFinancialHeading: 'Revenus et dépenses de l’immeuble',
        stepFinancialSupport:
          'Indiquez les montants annuels en dollars canadiens (CAD). Ces données serviront à préparer une estimation préliminaire par l’équipe Rentalyzer.',
        grossAnnualIncome: 'Revenus bruts annuels totaux',
        grossAnnualIncomeHelp:
          'Incluez tous les revenus de l’immeuble, lorsqu’ils s’appliquent : loyers résidentiels, loyers commerciaux, garages, stationnement, casiers, buanderie, internet, location d’équipement ou d’électroménagers, et tout autre revenu lié à l’immeuble.',
        expensesHeading: 'Dépenses annuelles réelles payées par le propriétaire',
        expensesHelp:
          'Indiquez les dépenses annuelles réellement payées. Rentalyzer pourra normaliser d’autres catégories (entretien, gestion, vacance, réserves) lors de l’analyse interne.',
        municipalTaxes: 'Taxes municipales (annuelles)',
        schoolTaxes: 'Taxes scolaires (annuelles)',
        insurance: 'Assurance (annuelle)',
        heatingElectricity: 'Chauffage et électricité (annuels)',
        otherExpenses: 'Autres dépenses (annuelles)',
        expenseBlankIsZeroHelp: 'Laissez vide pour indiquer 0 $.',
        moneyAmountPlaceholder: '0 $',
        otherExpensesDescription: 'Précisez les autres dépenses',
        otherExpensesDescriptionHelp: 'Requis lorsque le montant des autres dépenses est supérieur à zéro.',
        unknownExpense: 'Je ne sais pas',
        amountCad: 'Montant annuel (CAD)',
        stepBuildingHeading: 'Profil de l’immeuble',
        stepBuildingSupport: 'Indiquez l’année de construction et la composition des unités.',
        constructionYear: 'Année de construction',
        constructionYearHint:
          'Année indiquée dans nos informations. Vous pouvez la corriger au besoin.',
        constructionYearUnknown: 'Je ne connais pas l’année de construction',
        unitMixHeading: 'Composition des unités',
        unitOneAndHalf: '1½ / studio',
        unitTwoAndHalf: '2½',
        unitThreeAndHalf: '3½',
        unitFourAndHalf: '4½',
        unitFiveAndHalf: '5½',
        unitSixAndHalfPlus: '6½ et plus',
        unitCommercial: 'Unités commerciales',
        unitOther: 'Autres unités',
        unitDecrement: 'Diminuer',
        unitIncrement: 'Augmenter',
        unitMismatch:
          'Vous avez indiqué {declared} unités, alors que nos données en indiquent {imported}. Confirmez que la composition inscrite est exacte.',
        unitMismatchConfirm: 'Je confirme que la composition inscrite est exacte.',
        stepContactHeading: 'Coordonnées',
        stepContactSupport:
          'Nous utiliserons ces renseignements pour vous transmettre l’estimation préliminaire préparée par notre équipe.',
        next: 'Continuer',
        valuationProgress: (current, total) => `Étape ${current} sur ${total} — estimation`,
      }),
      en: Object.freeze({
        heading: 'What is my building worth?',
        support:
          'Our team will review the information you provide and prepare a preliminary estimate. This is not an instant automated valuation, and it is not a certified appraisal. No obligation.',
        cta: 'Receive my preliminary estimate',
        stepFinancialHeading: 'Building income and expenses',
        stepFinancialSupport:
          'Enter annual amounts in Canadian dollars (CAD). Rentalyzer’s team will use them to prepare a preliminary estimate.',
        grossAnnualIncome: 'Total gross annual income',
        grossAnnualIncomeHelp:
          'Include all property income, when applicable: residential rent, commercial rent, garages, parking, storage lockers, laundry, internet, equipment or appliance rentals, and any other property income.',
        expensesHeading: 'Actual annual expenses paid by the owner',
        expensesHelp:
          'Enter the annual expenses you actually pay. Rentalyzer may normalize other categories (maintenance, management, vacancy, reserves) during the internal analysis.',
        municipalTaxes: 'Municipal taxes (annual)',
        schoolTaxes: 'School taxes (annual)',
        insurance: 'Insurance (annual)',
        heatingElectricity: 'Heating and electricity (annual)',
        otherExpenses: 'Other expenses (annual)',
        expenseBlankIsZeroHelp: 'Leave blank to mean $0.',
        moneyAmountPlaceholder: '0 $',
        otherExpensesDescription: 'Describe other expenses',
        otherExpensesDescriptionHelp: 'Required when other expenses are greater than zero.',
        unknownExpense: 'I don’t know',
        amountCad: 'Annual amount (CAD)',
        stepBuildingHeading: 'Building profile',
        stepBuildingSupport: 'Provide the construction year and unit composition.',
        constructionYear: 'Construction year',
        constructionYearHint: 'Year shown in our records. You can correct it if needed.',
        constructionYearUnknown: 'I don’t know the construction year',
        unitMixHeading: 'Unit composition',
        unitOneAndHalf: '1½ / studio',
        unitTwoAndHalf: '2½',
        unitThreeAndHalf: '3½',
        unitFourAndHalf: '4½',
        unitFiveAndHalf: '5½',
        unitSixAndHalfPlus: '6½ and more',
        unitCommercial: 'Commercial units',
        unitOther: 'Other units',
        unitDecrement: 'Decrease',
        unitIncrement: 'Increase',
        unitMismatch:
          'You entered {declared} units, while our records show {imported}. Please confirm that the composition you entered is correct.',
        unitMismatchConfirm: 'I confirm that the composition entered is correct.',
        stepContactHeading: 'Contact details',
        stepContactSupport:
          'We will use these details to send you the preliminary estimate prepared by our team.',
        next: 'Continue',
        valuationProgress: (current, total) => `Step ${current} of ${total} — estimate`,
      }),
    }),
    property_sale: Object.freeze({
      fr: Object.freeze({
        heading: 'Je veux vendre mon immeuble',
        support:
          'Échangeons confidentiellement sur votre projet et la possibilité d’une offre d’achat directe. Sans obligation.',
        cta: 'Parler de mon immeuble',
      }),
      en: Object.freeze({
        heading: 'I want to sell my building',
        support:
          'Let’s discuss your project confidentially, including the possibility of a direct purchase offer. No obligation.',
        cta: 'Talk about my building',
      }),
    }),
  }),

  confirmation: Object.freeze({
    fr: Object.freeze({
      title: 'Demande reçue',
      body: 'Merci. Votre demande a bien été reçue. Nous vous recontacterons si un suivi est pertinent.',
      legacyNote: 'Une réponse précédente a déjà été reçue pour ce lien.',
      completed: 'Déjà transmis',
      continue: 'Choisir une autre option',
    }),
    en: Object.freeze({
      title: 'Request received',
      body: 'Thank you. Your request has been received. We will follow up if appropriate.',
      legacyNote: 'A previous response has already been received for this link.',
      completed: 'Already submitted',
      continue: 'Choose another option',
    }),
  }),

  content: Object.freeze({
    fr: Object.freeze({
      loading: 'Chargement…',
      unavailableTitle: 'Page indisponible',
      unavailableBody:
        'Ce lien n’est pas disponible pour le moment. Si le problème persiste, contactez Rentalyzer.',
      expiredTitle: 'Lien expiré',
      expiredBody: 'Cette page n’est plus disponible. Le code QR de votre lettre n’est plus valide.',
      languageToggle: 'EN',
      languagePair: 'FR | EN',
      confidential: 'Confidentiel',
      confidentialHint: 'Vos informations restent privées.',
      heroPrefix: 'Votre immeuble au',
      heroHeadline: 'Découvrez le potentiel de votre immeuble',
      heroBody:
        'Des pistes concrètes pour améliorer sa rentabilité, mieux comprendre sa valeur et, si vous le souhaitez, vendre directement votre immeuble.',
      heroCta: 'Découvrir le potentiel de mon immeuble',
      objectivesHeading: 'Qu’aimeriez-vous accomplir avec cet immeuble?',
      objectivesSubtext: 'Sélectionnez l’option qui correspond le mieux à vos objectifs.',
      back: 'Retour',
      fullName: 'Nom complet',
      email: 'Courriel',
      phone: 'Téléphone',
      ownershipAttestation:
        'Je confirme être propriétaire de cet immeuble ou être autorisé à transmettre ces renseignements.',
      // Draft for product/legal review before production.
      consent:
        'Je confirme être autorisé·e à fournir ces renseignements et j’accepte que Rentalyzer les utilise pour me contacter au sujet de l’immeuble et examiner les renseignements transmis.',
      privacy: 'Consulter la politique de confidentialité',
      submit: 'Envoyer',
      submitting: 'Envoi en cours…',
      retry: 'Réessayer le même envoi',
      refresh: 'Actualiser l’accès',
      errorSummary: 'Veuillez corriger les champs indiqués.',
      retryMessage:
        'Nous ne pouvons pas confirmer l’envoi. Vos réponses sont conservées sur cette page; réessayez sans les modifier ou actualisez l’accès.',
      mapTitle: 'Carte de l’immeuble',
      mapFallback: 'La carte interactive est temporairement indisponible.',
      mapRetry: 'Réessayer de charger la carte',
      incorrectProperty: 'Adresse incorrecte?',
      incorrectPropertyHint: 'Contactez-nous via le widget ci-dessous — le lien QR reste lié à cet immeuble.',
      contactWrite: 'Écrivez-moi',
      contactCall: 'Appelez-moi',
      contactQuestion: 'Une question?',
      contactExpand: 'Ouvrir le contact',
      contactCollapse: 'Fermer le contact',
      stepLabel: (current, total) => `Étape ${current} sur ${total}`,
      trustGift: 'Gratuit',
      errors: Object.freeze({
        required: 'Ce champ est requis.',
        emailInvalid: 'Entrez un courriel valide.',
        phoneInvalid: 'Entrez un numéro de téléphone valide.',
        consentRequired: 'Votre confirmation explicite est requise avant l’envoi.',
        ownershipRequired: 'Votre attestation est requise pour cette demande.',
        moneyInvalid: 'Entrez un montant annuel valide en CAD.',
        moneyNegative: 'Les montants ne peuvent pas être négatifs.',
        moneyUpperBound: 'Ce montant dépasse la limite acceptée.',
        otherExpensesDescriptionRequired: 'Précisez les autres dépenses lorsque le montant est supérieur à zéro.',
        constructionYearInvalid: 'Entrez une année de construction valide.',
        constructionYearXor: 'Indiquez une année ou choisissez « Je ne connais pas ».',
        unitMixRequired: 'Indiquez au moins une unité.',
        unitMixInvalid: 'Les nombres d’unités doivent être des entiers positifs ou zéro.',
        unitMismatchRequired: 'Confirmez que la composition inscrite est exacte.',
      }),
    }),
    en: Object.freeze({
      loading: 'Loading…',
      unavailableTitle: 'Page unavailable',
      unavailableBody:
        'This link is not available right now. If the problem continues, contact Rentalyzer.',
      expiredTitle: 'Link expired',
      expiredBody: 'This page is no longer available. The QR code on your letter is no longer valid.',
      languageToggle: 'FR',
      languagePair: 'FR | EN',
      confidential: 'Confidential',
      confidentialHint: 'Your information stays private.',
      heroPrefix: 'Your building at',
      heroHeadline: 'Discover your building’s potential',
      heroBody:
        'Practical ways to improve profitability, better understand value, and — if you wish — sell your building directly.',
      heroCta: 'Discover my building’s potential',
      objectivesHeading: 'What would you like to accomplish with this building?',
      objectivesSubtext: 'Select the option that best matches your goals.',
      back: 'Back',
      fullName: 'Full name',
      email: 'Email',
      phone: 'Phone',
      ownershipAttestation:
        'I confirm that I own this building or am authorized to share this information.',
      // Draft for product/legal review before production.
      consent:
        'I confirm that I am authorized to provide this information and agree that Rentalyzer may use it to contact me about the property and review the submitted information.',
      privacy: 'Read the Privacy Policy',
      submit: 'Submit',
      submitting: 'Submitting…',
      retry: 'Retry the same submission',
      refresh: 'Refresh access',
      errorSummary: 'Please correct the fields indicated below.',
      retryMessage:
        'We could not confirm submission. Your answers remain on this page; retry without changing them or refresh access.',
      mapTitle: 'Building map',
      mapFallback: 'The interactive map is temporarily unavailable.',
      mapRetry: 'Try loading the map again',
      incorrectProperty: 'Wrong address?',
      incorrectPropertyHint: 'Contact us via the widget below — this QR link remains bound to this building.',
      contactWrite: 'Email me',
      contactCall: 'Call me',
      contactQuestion: 'A question?',
      contactExpand: 'Open contact',
      contactCollapse: 'Close contact',
      stepLabel: (current, total) => `Step ${current} of ${total}`,
      trustGift: 'Free',
      errors: Object.freeze({
        required: 'This field is required.',
        emailInvalid: 'Enter a valid email address.',
        phoneInvalid: 'Enter a valid phone number.',
        consentRequired: 'Your explicit confirmation is required before submission.',
        ownershipRequired: 'Your attestation is required for this request.',
        moneyInvalid: 'Enter a valid annual amount in CAD.',
        moneyNegative: 'Amounts cannot be negative.',
        moneyUpperBound: 'This amount exceeds the accepted limit.',
        otherExpensesDescriptionRequired: 'Describe other expenses when the amount is greater than zero.',
        constructionYearInvalid: 'Enter a valid construction year.',
        constructionYearXor: 'Enter a year or choose “I don’t know”.',
        unitMixRequired: 'Enter at least one unit.',
        unitMixInvalid: 'Unit counts must be non-negative integers.',
        unitMismatchRequired: 'Confirm that the composition entered is correct.',
      }),
    }),
  }),
});

export function getEnabledObjectives(config = publicProspectPageConfig) {
  return config.objectives.filter((objective) => !objective.requiresGuide || config.guide.enabled);
}

export function getPublicProspectCopy(language, config = publicProspectPageConfig) {
  return config.content[language] || config.content.fr;
}
